import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Loader2, Lock, Send } from "lucide-react";

export default function AskAnvilModal({ doc, onClose }) {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [readable, setReadable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [deals, setDeals] = useState([]);
  const [dealId, setDealId] = useState("");
  const [section, setSection] = useState("proposal_draft");
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(false);

  useEffect(() => {
    if (pushOpen && deals.length === 0) {
      api.get("/bids").then((r) => setDeals((r.data || []).filter((d) => !["Won", "Lost", "Passed"].includes(d.pipeline_stage)))).catch(() => {});
    }
  }, [pushOpen, deals.length]);

  const ask = async () => {
    setLoading(true); setAnswer(""); setPushed(false); setPushOpen(false);
    try {
      const r = await api.post(`/library/documents/${doc.id}/ask`, { question: q });
      setAnswer(r.data.answer || "");
      setReadable(r.data.readable !== false);
    } catch (e) {
      setAnswer("Anvil couldn't answer right now. Try again.");
      setReadable(false);
    } finally { setLoading(false); }
  };

  const push = async () => {
    if (!dealId) return;
    setPushing(true);
    try {
      await api.post(`/bids/${dealId}/draft-insert`, { section, content: answer, citation: doc.name });
      setPushed(true); setPushOpen(false);
    } catch (e) { /* toast handled below */ }
    finally { setPushing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-xl p-6 space-y-4 shadow-xl"
        onClick={(e) => e.stopPropagation()} data-testid="ask-anvil-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-zinc-900 flex items-center gap-2">
            <AnvilIcon className="h-5 w-5 text-gold-dark" /> Ask Anvil
          </h2>
          <button onClick={onClose}><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <div className="text-xs text-zinc-500 truncate">About: <span className="text-zinc-800 font-medium">{doc.name}</span></div>

        {doc.sensitive ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-800" data-testid="ask-anvil-sensitive">
            <Lock className="h-4 w-4 shrink-0 mt-0.5" />
            This document is marked sensitive — its content is encrypted and Anvil can’t read it. Ask an admin to Reveal it if needed.
          </div>
        ) : (
          <>
            <textarea value={q} onChange={(e) => setQ(e.target.value)} rows={2}
              placeholder="e.g. What past-performance facts can I cite in a bid?"
              data-testid="ask-anvil-input"
              className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-gold-dark outline-none resize-none" />
            <button onClick={ask} disabled={loading} data-testid="ask-anvil-submit"
              className="w-full bg-zinc-900 text-white font-semibold py-2.5 rounded-md hover:bg-zinc-800 disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</> : "Ask Anvil"}
            </button>
            {answer && (
              <div data-testid="ask-anvil-answer"
                className={`text-sm whitespace-pre-wrap rounded-md p-3 border ${readable ? "bg-zinc-50 border-zinc-200 text-zinc-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                {answer}
              </div>
            )}
            {answer && readable && !pushed && (
              <div className="border-t border-zinc-100 pt-3">
                {!pushOpen ? (
                  <button onClick={() => setPushOpen(true)} data-testid="push-to-bid-btn"
                    className="flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-900 font-medium">
                    <Send className="h-4 w-4 text-gold-dark" /> Push into a bid…
                  </button>
                ) : (
                  <div className="space-y-2" data-testid="push-to-bid-panel">
                    <select value={dealId} onChange={(e) => setDealId(e.target.value)} data-testid="push-deal-select"
                      className="w-full bg-white border border-zinc-300 rounded-md px-2 py-2 text-sm text-zinc-900">
                      <option value="">Choose a deal…</option>
                      {deals.map((d) => <option key={d.id} value={d.id}>{d.title} {d.est_code ? `· ${d.est_code}` : ""}</option>)}
                    </select>
                    <select value={section} onChange={(e) => setSection(e.target.value)} data-testid="push-section-select"
                      className="w-full bg-white border border-zinc-300 rounded-md px-2 py-2 text-sm text-zinc-900">
                      <option value="proposal_draft">Proposal draft</option>
                      <option value="capture_plan">Capture plan</option>
                    </select>
                    <p className="text-[11px] text-zinc-500">Inserts as a cited, reviewable draft block (not final). The bid still runs through the compliance gate + officer send.</p>
                    <button onClick={push} disabled={pushing || !dealId} data-testid="push-confirm-btn"
                      className="w-full bg-zinc-900 text-white font-semibold py-2 rounded-md hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2">
                      {pushing ? <><Loader2 className="h-4 w-4 animate-spin" /> Inserting…</> : "Insert as draft"}
                    </button>
                  </div>
                )}
              </div>
            )}
            {pushed && (
              <div data-testid="push-success" className="text-sm rounded-md p-3 border bg-emerald-50 border-emerald-200 text-emerald-800">
                Inserted as a reviewable draft. Open the deal to review before use.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
